/**
 * スワイプクリップの整数寸法規則と座標変換 (§5.1 / §5.4 / §5.5)。
 * image-generator の docs/features/swipe-motion-clip-plan.md。
 *
 * **正本はここ (video-editor)**。image-generator の frontend/src/features/swipeClip/geometry.ts は
 * scripts/sync-swipe-clip.mjs による無改変コピー、backend/services/swipe_clip/params.py は同じ規則の Python 版。
 * 丸めは `floor(x + 0.5)` に固定し、`Math.round` / Python の `round` (偶数丸め) は使わない。
 */
import {
  SWIPE_CROP_MIN_PX,
  SWIPE_FRAMES,
  type SwipeClipParams,
  type SwipeFrame,
  type SwipeFramePosition,
  type SwipeRect,
  type SwipeSize,
} from './schema';

/** 非負の実数を「四捨五入」する (JS / Python で同じ結果になる形)。 */
export function roundHalfUp(value: number): number {
  return Math.floor(value + 0.5);
}

/** 切り詰め後の高さ (px)。既存 _apply_crop と同じ切り捨て + 下限 (§5.4)。 */
export function cropHeightPx(imageHeight: number, endPct: number): number {
  const cropped = Math.floor((imageHeight * endPct) / 100);
  return Math.min(imageHeight, Math.max(SWIPE_CROP_MIN_PX, cropped));
}

/** 縮小後の整数寸法 (§5.5-3)。拡大はしない。 */
export function resizedSize(imageWidth: number, croppedHeight: number, screenWidth: number): SwipeSize {
  const width = Math.min(imageWidth, screenWidth);
  const height = Math.max(1, roundHalfUp((croppedHeight * width) / imageWidth));
  return { width, height };
}

/** 枠無しのスクリーン矩形 = 出力全面 (§5.1・§3-12)。 */
export function fullScreenRect(output: { width: number; height: number }): SwipeRect {
  return { x: 0, y: 0, width: output.width, height: output.height };
}

// ============================================================
// 枠 (スマホ風 / PC 風の筐体・§11.1)
// ============================================================

/** 筐体 (角丸の矩形)。 */
export interface SwipeFrameBody extends SwipeRect {
  radius: number;
}

/**
 * 枠の配置 (出力 px・すべて整数)。screenRect / screenRadius がレンダ入力に入り、残りは SwipeClip.tsx が筐体を描くのに使う。
 * FE / VE はこの関数、BE は params.py の frame_layout_for (screenRect / screenRadius だけ) が同じ規則で持つ。
 * 乗算は「整数 × 整数 / 整数」の形に固定し、JS と Python で同じ浮動小数になるようにする。
 */
export interface SwipeFrameLayout {
  frame: SwipeFrame;
  screenRect: SwipeRect;
  screenRadius: number;
  /** 筐体。none のときは無し。 */
  body: SwipeFrameBody | null;
  /** 筐体の縁の太さ (画面との差)。 */
  bezel: number;
  /** phone: 画面上部の黒い島 (ノッチ)。画面の上端から top だけ下げて中央寄せ。 */
  island: { width: number; height: number; top: number } | null;
  /** pc: 筐体の下の台座 (キーボード側)。 */
  base: (SwipeRect & { radius: number; lipWidth: number; lipHeight: number }) | null;
}

/** 出力で選べる枠。どの縦横でも全部 (2026-10-13: 当初は縦長 = スマホ・横長 = PC に限っていたが、横長にスマホ・縦長に PC も
 *  置けるよう解放した。既定は none のまま)。引数は将来の制限のために残す。 */
export function framesAllowedFor(output: { width: number; height: number }): SwipeFrame[] {
  void output; // 将来の制限のために引数は残す (eslint-disable コメントは image-generator の lint:url 設定にルールが無く落ちる)
  return [...SWIPE_FRAMES];
}

export function isFrameAllowed(frame: SwipeFrame, output: { width: number; height: number }): boolean {
  return framesAllowedFor(output).includes(frame);
}

/** 枠の位置の端からの余白 (出力の短辺の 12%。2026-10-13 に 5% から広げた: 際すぎるため)。 */
export const SWIPE_FRAME_EDGE_MARGIN_PCT = 12;
export function frameEdgeMargin(output: { width: number; height: number }): number {
  return Math.floor((Math.min(output.width, output.height) * SWIPE_FRAME_EDGE_MARGIN_PCT) / 100);
}

/**
 * 枠の位置 → 配置の起点。横長 (正方形を含む) は x 方向 (start = 左・end = 右)、縦長は y 方向 (start = 上・end = 下)。
 * `extent` は動かす方向の筐体の全幅 (PC は台座込み)、`size` はその方向の出力の寸法。center は従来どおり中央。
 * 端からの余白は frameEdgeMargin (短辺の 12%)。筐体が大きくて余白が取れないときは 0〜(size − extent) に収める。
 */
function positionedOffset(position: SwipeFramePosition, size: number, extent: number, margin: number): number {
  const max = Math.max(0, size - extent);
  if (position === 'start') return Math.min(margin, max);
  if (position === 'end') return Math.max(0, max - margin);
  return Math.floor(max / 2);
}

function phoneFrameLayout(output: { width: number; height: number }, position: SwipeFramePosition): SwipeFrameLayout {
  const W = output.width;
  const H = output.height;
  // 筐体: 高さは出力の 90%、幅は高さの 46% (一般的なスマホの比率)。幅が出力の 90% を超えるときは幅から決め直す
  let bodyH = Math.floor((H * 90) / 100);
  let bodyW = Math.floor((bodyH * 46) / 100);
  const maxW = Math.floor((W * 90) / 100);
  if (bodyW > maxW) {
    bodyW = maxW;
    bodyH = Math.floor((bodyW * 100) / 46);
  }
  const bezel = Math.max(2, roundHalfUp((bodyW * 35) / 1000));
  const bodyRadius = roundHalfUp((bodyW * 14) / 100);
  const margin = frameEdgeMargin(output);
  const landscape = W >= H;
  const bodyX = landscape ? positionedOffset(position, W, bodyW, margin) : Math.floor((W - bodyW) / 2);
  const bodyY = landscape ? Math.floor((H - bodyH) / 2) : positionedOffset(position, H, bodyH, margin);
  return {
    frame: 'phone',
    screenRect: { x: bodyX + bezel, y: bodyY + bezel, width: bodyW - 2 * bezel, height: bodyH - 2 * bezel },
    screenRadius: Math.max(0, bodyRadius - bezel),
    body: { x: bodyX, y: bodyY, width: bodyW, height: bodyH, radius: bodyRadius },
    bezel,
    island: { width: roundHalfUp((bodyW * 28) / 100), height: roundHalfUp((bodyW * 8) / 100), top: roundHalfUp((bodyW * 3) / 100) },
    base: null,
  };
}

function pcFrameMetrics(bodyW: number) {
  const bezel = Math.max(2, roundHalfUp((bodyW * 18) / 1000));
  const screenW = bodyW - 2 * bezel;
  // 画面は 16:10 (ノート PC)
  const screenH = roundHalfUp((screenW * 10) / 16);
  const bodyH = screenH + 2 * bezel;
  const baseH = Math.max(2, roundHalfUp((bodyW * 28) / 1000));
  return { bezel, screenW, screenH, bodyH, baseH, total: bodyH + baseH };
}

function pcFrameLayout(output: { width: number; height: number }, position: SwipeFramePosition): SwipeFrameLayout {
  const W = output.width;
  const H = output.height;
  // 筐体の幅は出力の 74%。筐体 + 台座の高さが出力の 88% を超えるときは幅を縮める (縮めた後の丸めで超える分は 1px ずつ詰める)
  let bodyW = Math.floor((W * 74) / 100);
  let m = pcFrameMetrics(bodyW);
  const maxTotal = Math.floor((H * 88) / 100);
  if (m.total > maxTotal) {
    bodyW = Math.floor((bodyW * maxTotal) / m.total);
    m = pcFrameMetrics(bodyW);
    while (m.total > maxTotal && bodyW > 8) {
      bodyW -= 1;
      m = pcFrameMetrics(bodyW);
    }
  }
  const overhang = roundHalfUp((bodyW * 8) / 100);
  const margin = frameEdgeMargin(output);
  const landscape = W >= H;
  // 横方向は台座 (左右に overhang だけ広い) を基準に寄せる
  const bodyX = landscape ? positionedOffset(position, W, bodyW + 2 * overhang, margin) + overhang : Math.floor((W - bodyW) / 2);
  const bodyY = landscape ? Math.floor((H - m.total) / 2) : positionedOffset(position, H, m.total, margin);
  return {
    frame: 'pc',
    screenRect: { x: bodyX + m.bezel, y: bodyY + m.bezel, width: m.screenW, height: m.screenH },
    screenRadius: roundHalfUp((bodyW * 4) / 1000),
    body: { x: bodyX, y: bodyY, width: bodyW, height: m.bodyH, radius: roundHalfUp((bodyW * 12) / 1000) },
    bezel: m.bezel,
    island: null,
    base: {
      x: bodyX - overhang,
      y: bodyY + m.bodyH,
      width: bodyW + 2 * overhang,
      height: m.baseH,
      radius: Math.floor(m.baseH / 2),
      lipWidth: roundHalfUp((bodyW * 14) / 100),
      lipHeight: Math.max(1, Math.floor((m.baseH * 45) / 100)),
    },
  };
}

/** 枠 → 配置。none は出力全面 (角丸 0)。position 省略 = center。 */
export function frameLayoutFor(frame: SwipeFrame, output: { width: number; height: number }, position: SwipeFramePosition = 'center'): SwipeFrameLayout {
  if (frame === 'phone') return phoneFrameLayout(output, position);
  if (frame === 'pc') return pcFrameLayout(output, position);
  return { frame: 'none', screenRect: fullScreenRect(output), screenRadius: 0, body: null, bezel: 0, island: null, base: null };
}

/** 保存形の枠の配置 (look.frame / look.framePosition と output から)。 */
export function frameLayoutForParams(params: Pick<SwipeClipParams, 'look' | 'output'>): SwipeFrameLayout {
  return frameLayoutFor(params.look.frame, params.output, params.look.framePosition ?? 'center');
}

/** 保存形のスクリーン矩形。 */
export function screenRectFor(params: Pick<SwipeClipParams, 'look' | 'output'>): SwipeRect {
  return frameLayoutForParams(params).screenRect;
}

export interface SwipeGeometry {
  /** image の座標系での切り詰め後の高さ (切り詰め無しなら元の高さ)。 */
  cropHeight: number;
  /** 切り詰め + 縮小後の整数寸法。 */
  virtual: SwipeSize;
  /** virtual → スクリーン矩形の幅に合わせる倍率。 */
  imageScale: number;
  /** 画面に見える高さ (virtual の高さに対する %)。 */
  viewportPct: number;
  /** virtual 基準の開始位置 (画面上端)。 */
  s0: number;
  /** virtual 基準の終了地点 (画面下端)。 */
  sEndEdge: number;
  /** 描画時の拡大率 (1 以上。元画像の幅 < スクリーン矩形の幅 のとき > 1)。 */
  upscaleRatio: number;
}

/**
 * 保存形 (元画像基準) からレンダ入力の幾何を組む。
 * - cropToEnd.enabled: 仮想画像 = 元画像の上 `endPct` %。s0 は実際に採用した比率 rActual で変換し、sEndEdge は 100
 * - それ以外: 仮想画像 = 元画像全体。s0 / sEndEdge は range のまま
 */
export function buildSwipeGeometry(params: SwipeClipParams, screenRect: SwipeRect): SwipeGeometry {
  const W = params.source.width;
  const H = params.source.height;
  const cropHeight = params.cropToEnd.enabled ? cropHeightPx(H, params.range.endPct) : H;
  const virtual = resizedSize(W, cropHeight, screenRect.width);
  const imageScale = screenRect.width / virtual.width;
  const imageDisplayH = virtual.height * imageScale;
  const viewportPct = (screenRect.height / imageDisplayH) * 100;

  let s0 = params.range.startPct;
  let sEndEdge = params.range.endPct;
  if (params.cropToEnd.enabled) {
    const rActual = cropHeight / H;
    s0 = Math.min(100, params.range.startPct / rActual);
    sEndEdge = 100;
  }
  return {
    cropHeight,
    virtual,
    imageScale,
    viewportPct,
    s0,
    sEndEdge,
    upscaleRatio: Math.max(1, screenRect.width / W),
  };
}

/** 画像が「縦長」で入口を出すか (§4.1・§13-1: 出力の縦横比の 1.5 倍以上)。 */
export const SWIPE_TALL_RATIO = 1.5;
export function isTallEnough(image: SwipeSize, output: SwipeSize): boolean {
  return image.height / image.width >= (output.height / output.width) * SWIPE_TALL_RATIO;
}

// ============================================================
// レンダ入力 (SwipeRenderInput) からの導出 (SwipeClip.tsx と calculateMetadata が共用)
// ============================================================

export interface SwipeRenderGeometry {
  /** virtual → スクリーン矩形の幅に合わせる倍率。 */
  imageScale: number;
  /** 仮想画像要素の寸法 (出力 px)。 */
  elementWidth: number;
  elementHeight: number;
  /** 仮想画像要素の中に置く画像の高さ (出力 px)。crop.height → virtual.height の写像に合わせる (§6.1)。 */
  imageHeight: number;
  /** 画面に見える高さ (virtual の高さに対する %)。 */
  viewportPct: number;
}

export function renderInputGeometry(input: {
  image: SwipeSize;
  crop: { height: number };
  virtual: SwipeSize;
  screenRect: SwipeRect;
}): SwipeRenderGeometry {
  const imageScale = input.screenRect.width / input.virtual.width;
  const elementWidth = input.virtual.width * imageScale;
  const elementHeight = input.virtual.height * imageScale;
  const imageHeight = (elementHeight * input.image.height) / input.crop.height;
  const viewportPct = (input.screenRect.height / elementHeight) * 100;
  return { imageScale, elementWidth, elementHeight, imageHeight, viewportPct };
}

/** 保存形 + スクリーン矩形 → レンダ入力の幾何部分 (image / crop / virtual / s0 / sEndEdge)。FE プレビューは元画像を渡す。
 *  screenRadius を省略すると look.frame の配置 (frameLayoutFor) から取る。 */
export function buildRenderInputGeometry(params: SwipeClipParams, screenRect: SwipeRect, mode: 'preview' | 'render', screenRadius?: number) {
  const geo = buildSwipeGeometry(params, screenRect);
  const image = mode === 'preview' ? { width: params.source.width, height: params.source.height } : geo.virtual;
  const crop = { height: mode === 'preview' ? geo.cropHeight : geo.virtual.height };
  const radius = screenRadius ?? frameLayoutForParams(params).screenRadius;
  return { image, crop, virtual: geo.virtual, s0: geo.s0, sEndEdge: geo.sEndEdge, screenRect, screenRadius: radius };
}
